<script setup lang="ts">
import { computed } from 'vue'

import { useTurnProgressLabel } from '../composables/useOrpheusTurnProgress'
import type { ChatMessage } from '../models/orpheus.types'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui'

const props = defineProps<{ message: ChatMessage }>()
const emit = defineEmits<{ cancel: [] }>()
const { t } = useI18n()

// The main chat's live progress line (ORP-28228): the phase the backend reports for this turn, in
// place of the generic connecting/responding label. Absent in the feature chat panel.
const progress = useTurnProgressLabel(() => props.message)

const active = computed(
  () => props.message.streamState === 'connecting' || props.message.streamState === 'streaming',
)
const stateLabel = computed(() => {
  if (active.value && progress.value) return progress.value.text
  if (props.message.streamState === 'connecting') return t('orpheus.stream.connecting')
  if (props.message.streamState === 'streaming') return t('orpheus.stream.responding')
  if (props.message.streamState === 'canceled') return t('orpheus.stream.canceled')
  if (props.message.streamState === 'reconnectable-error') {
    return t('orpheus.stream.reconnectable')
  }
  if (props.message.streamState === 'terminal-error') return t('orpheus.stream.terminalError')
  return t('orpheus.stream.completed')
})
</script>

<template>
  <div
    class="streaming-message flex flex-col gap-3xs text-muted text-[0.8125rem]"
    :data-state="message.streamState"
    aria-live="polite"
  >
    <div class="flex items-center gap-xs">
      <span
        v-if="active"
        class="streaming-message__indicator animate-pulse [animation-duration:1.2s] motion-reduce:animate-none w-2 h-2 flex-none rounded-full bg-orpheus"
        aria-hidden="true"
      />
      <Transition
        mode="out-in"
        enter-active-class="transition-opacity duration-300 motion-reduce:transition-none"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-200 motion-reduce:transition-none"
        leave-to-class="opacity-0"
      >
        <span :key="progress?.key ?? 'state'" data-testid="orpheus-waiting-progress">
          {{ stateLabel }}
        </span>
      </Transition>
      <Button v-if="active" size="xs" variant="outline" @click="emit('cancel')">
        {{ t('orpheus.composer.stop') }}
      </Button>
    </div>
    <span v-if="active" class="streaming-message__hint text-[0.75rem]">
      {{ t('orpheus.stream.longWaitHint') }}
    </span>
  </div>
</template>
